import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  EmailOutlined as EmailIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ALERT_CONDITION_TYPES, ALERT_ACTION_TYPES } from '../../../shared/appConstants';
import { ICONS } from '../../../shared/icons';

const AlertRuleCard = ({ 
  alert, 
  onEdit, 
  onDelete, 
  onToggle 
}) => {
  const { t } = useTranslation();
  
  const getConditionLabel = (type) => {
    const labels = {
      [ALERT_CONDITION_TYPES.PAST_DUE_DATE]: t('projectAlerts.conditionTypes.pastDueDate'),
      [ALERT_CONDITION_TYPES.DUE_IN_X_DAYS]: t('projectAlerts.conditionTypes.dueInXDays'),
      [ALERT_CONDITION_TYPES.STUCK_IN_PROGRESS]: t('projectAlerts.conditionTypes.stuckInProgress'),
      [ALERT_CONDITION_TYPES.HIGH_PRIORITY_OVERDUE]: t('projectAlerts.conditionTypes.highPriorityOverdue'),
      [ALERT_CONDITION_TYPES.NO_ACTIVITY_FOR_X_DAYS]: t('projectAlerts.conditionTypes.noActivityForXDays'),
      [ALERT_CONDITION_TYPES.DEPENDENCY_BLOCKED]: t('projectAlerts.conditionTypes.dependencyBlocked'),
      [ALERT_CONDITION_TYPES.STUCK_IN_REVIEW]: t('projectAlerts.conditionTypes.stuckInReview')
    };
    return labels[type] || type;
  };

  const getActionIcon = (type) => {
    switch (type) {
      case ALERT_ACTION_TYPES.EMAIL:
        return <EmailIcon className="action-icon" />;
      case ALERT_ACTION_TYPES.SLACK:
        return <MessageIcon className="action-icon" />;
      default:
        return null;
    }
  };
  
  const getActionTypeLabel = (type) => {
    return t(`projectAlerts.actionTypes.${type}`);
  };

  const handleToggle = (event) => {
    onToggle(alert.id, event.target.checked);
  };

  return (
    <div className={`alert-rule-row ${alert.enabled ? 'enabled' : 'disabled'}`}>
      <div className="alert-rule-header">
        <div className="alert-rule-info">
          <Typography variant="subtitle1" fontWeight="500" color="text.primary">
            {alert.name}
          </Typography>
          <Chip 
            label={alert.enabled ? t('projectAlerts.status.enabled') : t('projectAlerts.status.disabled')}
            size="small"
            className={`alert-status-chip ${alert.enabled ? 'enabled' : 'disabled'}`}
          />
        </div>
        
        <div className="alert-rule-actions">
          <div className="toggle-container">
            <FormControlLabel
              control={
                <Switch
                  checked={alert.enabled}
                  onChange={handleToggle}
                  className="alert-switch"
                />
              }
              label={
                <span className="toggle-label">
                  {alert.enabled ? t('projectAlerts.status.enabled') : t('projectAlerts.status.disabled')}
                </span>
              }
            />
          </div>
          
          <Tooltip title={t('projectAlerts.tooltips.edit')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
            <IconButton
              onClick={() => onEdit(alert)}
              className="alert-edit-button"
          >
            <Box component="img" src={ICONS.editIcon} alt="" aria-hidden="true" className="alert-button-icon" />
          </IconButton>
          </Tooltip>
          
          <Tooltip title={t('projectAlerts.tooltips.delete')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
            <IconButton
              onClick={() => onDelete(alert.id)}
              className="alert-delete-button"
          >
            <DeleteIcon />
          </IconButton>
          </Tooltip>
        </div>
      </div>
      
      <div className="alert-rule-details">
        <div className="alert-condition">
          <Typography variant="body2" color="text.secondary" className="alert-condition-label">
            {t('projectAlerts.fields.condition')}:
          </Typography>
          <Chip 
            label={getConditionLabel(alert.condition?.type)} 
            size="small" 
            className="alert-condition-chip"
          />
          <Chip 
            label={alert.condition?.threshold || 'N/A'} 
            size="small"
            variant="outlined"
          />
        </div>
        
        <div>
          <div className="alert-action-type-row">
            <Typography variant="body2" color="text.secondary" className="alert-action-type-label">
              {t('projectAlerts.fields.actionTypeLabel')}:
            </Typography>
            {alert.actions?.map((action, index) => (
              <Chip
                key={index}
                icon={getActionIcon(action.type)}
                label={getActionTypeLabel(action.type)}
                size="small"
                className={`alert-action-type-chip ${action.type}`}
              />
            ))}
          </div>
          {alert.actions?.map((action, index) => (
            action.recipients && action.recipients.length > 0 && action.type === ALERT_ACTION_TYPES.EMAIL && (
              <div key={index} className="alert-recipients-row">
                <Typography variant="body2" color="text.secondary" className="alert-recipients-label">
                  {t('projectAlerts.fields.emailRecipients')}
                </Typography>
                <div className="recipients-chips">
                  {action.recipients.map((recipient, recipientIndex) => (
                    <Chip
                      key={recipientIndex}
                      label={recipient}
                      size="small"
                      className={`recipient-chip ${action.type}`}
                    />
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlertRuleCard;
